/**
 * Authentication Context
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { tokenStorage } from '../utils/tokenStorage';
import { userService } from '../services/userService';

const AuthContext = createContext(undefined);

/**
 * AuthProvider Component
 * Wraps the application and provides authentication context
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchUserInfo = useCallback(async () => {
    try {
      const info = await userService.getUserInfo();
      setUserInfo(info);
      setUser({ username: info.username });
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  }, []);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (tokenStorage.hasTokens()) {
          setIsAuthenticated(true);
          await fetchUserInfo();
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        tokenStorage.clearTokens();
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [fetchUserInfo]);

  /**
   * Login function
   */
  const login = useCallback(
    async (credentials) => {
      try {
        setIsLoading(true);
        await authService.login(credentials);
        setIsAuthenticated(true);
        await fetchUserInfo();
        navigate('/dashboard', { replace: true });
      } catch (error) {
        tokenStorage.clearTokens();
        setIsAuthenticated(false);
        setUser(null);
        setUserInfo(null);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [navigate, fetchUserInfo]
  );

  /**
   * Logout function
   */
  const logout = useCallback(() => {
    authService.logout();
    sessionStorage.removeItem('reports_landing_seen');
    setUser(null);
    setUserInfo(null);
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  }, [navigate]);

  /**
   * Refresh access token
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      await authService.refreshAccessToken();
    } catch (error) {
      logout();
      throw error;
    }
  }, [logout]);

  const value = {
    user,
    userInfo,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshAccessToken,
    fetchUserInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 * Provides access to authentication context
 * @returns {Object} Auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
